import React, { useState, useEffect } from "react";
import PeakBackground from "../static/peak-background.svg";
import { useDoubleSidebar } from "@vojomo-core/shared";
import { Tooltip, Switch } from "antd";
import { TbPencil } from "react-icons/tb";
import { ReadOnlyEditor } from "components/ReadyOnlyEditor";

const ballPurple = { fill: "#d989ff", stroke: "#8e30dc" };
const ballGrey = { fill: "#c2c6cb", stroke: "#8476a1" };

interface SessionMountainBkgProps {
  vision: string;
  mission: string;
  peakAmbitionDate: string;
  peakAmbitionContent: string;
  headlineImgUrl?: string | null;
  peakObjectiveDate: string;
  peakObjectiveContent: string;
  threeYearObjectivesDate: string;
  threeYearObjectivesContent: string;
  oneYearObjectivesDate: string;
  oneYearObjectivesContent: string;
  qObjectivesDate: string;
  qObjectivesContent: string;
  hasWriteAccess: boolean;
  embedded?: boolean;
  onSave: (blockName: string, content: string) => Promise<void>;
}

export const SessionMountainBkg: React.FC<SessionMountainBkgProps> = ({
  vision,
  mission,
  peakAmbitionDate,
  peakAmbitionContent,
  headlineImgUrl,
  peakObjectiveDate,
  peakObjectiveContent,
  threeYearObjectivesDate,
  threeYearObjectivesContent,
  oneYearObjectivesDate,
  oneYearObjectivesContent,
  qObjectivesDate,
  qObjectivesContent,
  hasWriteAccess,
  embedded = false,
  onSave,
}) => {
  const { isCollapsed } = useDoubleSidebar();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  useEffect(() => {
    const onResize = () => {
      setWindowWidth(window.innerWidth);
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [peakAmbitionHover, setPeakAmbitionHover] = useState(false);
  const [peakObjectiveHover, setPeakObjectiveHover] = useState(false);
  const [threeYearObjectivesHover, setThreeYearObjectivesHover] = useState(false);
  const [oneYearObjectivesHover, setOneYearObjectivesHover] = useState(false);
  const [qObjectivesHover, setQObjectivesHover] = useState(false);
  const [visionHover, setVisionHover] = useState(false);
  const [missionHover, setMissionHover] = useState(false);

  const [editingBlock, setEditingBlock] = useState<string | null>(null);
  const [editContent, setEditContent] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [showPeakAmbitionImage, setShowPeakAmbitionImage] = useState(true);

  useEffect(() => {
    setEditContent({
      Vision: vision || "",
      Mission: mission || "",
      "Peak Ambition": peakAmbitionContent || "",
      "Peak Ambition Objectives": peakObjectiveContent || "",
      "3 Year Objectives": threeYearObjectivesContent || "",
      "1 Year Objectives": oneYearObjectivesContent || "",
      "Quarterly Objectives": qObjectivesContent || "",
    });
  }, [
    vision, mission, peakAmbitionContent, peakObjectiveContent,
    threeYearObjectivesContent, oneYearObjectivesContent, qObjectivesContent,
  ]);

  const handleClick = (activeHover: string) => {
    setPeakAmbitionHover(activeHover === "peakAmbition" ? !peakAmbitionHover : false);
    setPeakObjectiveHover(activeHover === "peakObjective" ? !peakObjectiveHover : false);
    setThreeYearObjectivesHover(activeHover === "threeYearObjectives" ? !threeYearObjectivesHover : false);
    setOneYearObjectivesHover(activeHover === "oneYearObjectives" ? !oneYearObjectivesHover : false);
    setQObjectivesHover(activeHover === "qObjectives" ? !qObjectivesHover : false);
  };

  const handleReset = () => {
    setPeakAmbitionHover(false);
    setPeakObjectiveHover(false);
    setThreeYearObjectivesHover(false);
    setOneYearObjectivesHover(false);
    setQObjectivesHover(false);
    setVisionHover(false);
    setMissionHover(false);
    setEditingBlock(null);
  };

  const handleSaveBlock = async (blockName: string) => {
    const content = editContent[blockName] ?? "";
    setSaving(true);
    try {
      await onSave(blockName, content);
      setEditingBlock(null);
    } finally {
      setSaving(false);
    }
  };

  const editButton = (blockName: string) => {
    const isEditing = editingBlock === blockName;
    const isPeakAmbitionShowingImage =
      blockName === "Peak Ambition" && headlineImgUrl && showPeakAmbitionImage;
    if (isPeakAmbitionShowingImage) return null;
    if (!hasWriteAccess) return null;
    return (
      <Tooltip title={isEditing ? "Save" : "Edit inline"}>
        <div
          style={{
            cursor: saving ? "default" : "pointer",
            color: "#23546C",
            display: "flex",
            alignItems: "center",
            fontSize: "18px",
            transition: "opacity 0.2s",
            opacity: saving ? 0.5 : 1,
          }}
          onClick={() => {
            if (saving) return;
            if (isEditing) {
              handleSaveBlock(blockName);
            } else {
              setEditingBlock(blockName);
            }
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = saving ? "0.5" : "0.7")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = saving ? "0.5" : "1")}
        >
          {isEditing ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <TbPencil />
          )}
        </div>
      </Tooltip>
    );
  };

  const buildRoadmapItems = ({
    blockName,
    label,
    x,
    y,
    width,
    height,
    date,
    content,
    yOffsetOverride,
    blankYOffsetOverride,
    styleOverride,
  }: {
    blockName: string;
    label?: string;
    x: number;
    y: number;
    width: string;
    height: string;
    date: string;
    content: string | null;
    yOffsetOverride?: number;
    blankYOffsetOverride?: number;
    styleOverride?: React.CSSProperties;
  }) => {
    const status = content !== null && content !== "" ? "done" : "not done";
    const ballColor = status === "done" ? ballPurple : ballGrey;

    const blockLabel =
      label ||
      (blockName === "Quarterly Objectives"
        ? "Next Quarter"
        : blockName.replace("1", "One").replace("3", "Three"));
    const blockTitle =
      label ||
      (blockName === "Quarterly Objectives"
        ? "Quarterly Objectives"
        : blockName.replace("1", "One").replace("3", "Three"));

    const hoverMap: Record<string, string> = {
      "Peak Ambition": "peakAmbition",
      "Peak Ambition Objectives": "peakObjective",
      "3 Year Objectives": "threeYearObjectives",
      "1 Year Objectives": "oneYearObjectives",
      "Quarterly Objectives": "qObjectives",
    };

    const roadmapItemText = (
      <foreignObject x={x} y={y} width={225} height={75}>
        <div onClick={() => handleClick(hoverMap[blockName])}>
          <p style={{ margin: 0, textDecoration: "underline", textUnderlineOffset: "4px",
            fontSize: "22px", fontWeight: "500", userSelect: "none", color: "#23546C" }}>
            {date}
          </p>
          <p style={{ fontSize: "14px", textDecoration: "none", margin: 0,
            color: "#23546C", userSelect: "none" }}>
            ({blockName === "Quarterly Objectives" ? blockLabel.toLowerCase() : blockTitle.toLowerCase()})
          </p>
        </div>
      </foreignObject>
    );

    const isEditing = editingBlock === blockName;
    const currentContent = editContent[blockName] ?? content ?? "";

    const roadmapItemModalInner = () => {
      let modalY = y - 115 + (yOffsetOverride || 0);
      let modalHeight = height;
      let modalWidth = width;
      const modalPadding = "20px 50px 35px 50px";
      let modalInner: React.ReactNode;

      const contentArea = (
        <div style={{ maxWidth: "100%", maxHeight: "100%", overflowY: "auto",
          border: isEditing ? "2px solid #d0d0d0" : "none", borderRadius: "4px",
          paddingRight: isEditing ? "8px" : "0", paddingLeft: isEditing ? "8px" : "0" }}>
          {isEditing ? (
            <textarea
              value={currentContent}
              onChange={(e) =>
                setEditContent((prev) => ({ ...prev, [blockName]: e.target.value }))
              }
              style={{ width: "100%", height: "180px", resize: "none",
                border: "none", outline: "none", fontSize: "14px",
                fontFamily: "inherit", lineHeight: "1.5" }}
            />
          ) : (
            <ReadOnlyEditor content={currentContent} />
          )}
        </div>
      );

      modalInner = (
        <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between",
            alignItems: "flex-start" }}>
            <p style={{ fontSize: "22px", fontWeight: "500", userSelect: "none",
              color: "#15224A", marginBottom: "20px", marginTop: "20px", ...styleOverride }}>
              {blockTitle}
            </p>
            {editButton(blockName)}
          </div>
          {contentArea}
        </div>
      );

      // Blank content (not peak ambition)
      if (
        (blockName !== "Peak Ambition" && (content === "" || content === null)) ||
        (blockName === "Peak Ambition" && !headlineImgUrl && !content)
      ) {
        modalY = y - 65 + (blankYOffsetOverride || 0);
        modalHeight = "17%";
        modalWidth = "20%";
      } else if (blockName === "Peak Ambition" && !headlineImgUrl && content) {
        modalY = y - 65;
        modalHeight = "20%";
        modalWidth = "20%";
      } else if (blockName === "Peak Ambition") {
        const hasImage = !!headlineImgUrl;
        const hasText = !!content;
        const showToggle = hasImage && hasText;

        modalInner = (
          <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <div style={{ display: "flex", justifyContent: "space-between",
              alignItems: "center", marginBottom: "10px" }}>
              {showToggle && (
                <div style={{ display: "flex", alignItems: "center", gap: "8px",
                  fontSize: "14px", color: "#23546C" }}>
                  <span style={{ fontWeight: showPeakAmbitionImage ? "400" : "600",
                    cursor: "pointer" }}
                    onClick={() => setShowPeakAmbitionImage(false)}>
                    Press Release
                  </span>
                  <Switch checked={showPeakAmbitionImage}
                    onChange={setShowPeakAmbitionImage} size="small" />
                  <span style={{ fontWeight: showPeakAmbitionImage ? "600" : "400",
                    cursor: "pointer" }}
                    onClick={() => setShowPeakAmbitionImage(true)}>
                    Headline Image
                  </span>
                </div>
              )}
              <div style={{ marginLeft: "auto" }}>{editButton(blockName)}</div>
            </div>
            <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
              {showPeakAmbitionImage && hasImage ? (
                <img src={headlineImgUrl!} style={{ width: "100%", height: "100%",
                  objectFit: "contain", alignSelf: "center" }} alt="Peak Ambition" />
              ) : hasText ? (
                <div style={{ border: isEditing ? "2px solid #d0d0d0" : "none",
                  borderRadius: "4px" }}>
                  {isEditing ? (
                    <textarea
                      value={currentContent}
                      onChange={(e) =>
                        setEditContent((prev) => ({ ...prev, [blockName]: e.target.value }))
                      }
                      style={{ width: "100%", height: "180px", resize: "none",
                        border: "none", outline: "none", fontSize: "14px",
                        fontFamily: "inherit", lineHeight: "1.5" }}
                    />
                  ) : (
                    <ReadOnlyEditor content={currentContent} />
                  )}
                </div>
              ) : (
                <p style={{ padding: "10px", color: "#50608E" }}>
                  No content available yet...
                </p>
              )}
            </div>
          </div>
        );
      }

      return { modalY, modalHeight, modalPadding, modalWidth, modalInner };
    };

    const { modalY, modalHeight, modalPadding, modalWidth, modalInner } = roadmapItemModalInner();

    const roadmapItemModal = (
      <>
        <foreignObject x={x + 250} y={modalY} width={modalWidth} height={modalHeight}>
          <div id="roadmap-item-modal-parent" style={{ position: "relative", width: "100%",
            height: "100%", scrollbarWidth: "thin", scrollbarGutter: "stable",
            padding: modalPadding, display: "flex", flexDirection: "column",
            background: "white", borderRadius: "5px" }}>
            {modalInner}
          </div>
        </foreignObject>
        <foreignObject x={x + 143} y={y - 32} width={200} height={100}>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center",
            width: "100%", height: "100%", transform: "rotate(-90deg)" }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#FFFFFF"
              width="200" height="100">
              <path d="M0 0h24v24H0z" fill="none" />
              <path d="M7 14l5-5 5 5z" />
            </svg>
          </div>
        </foreignObject>
      </>
    );

    return { roadmapItemText, roadmapItemModal, ballColor };
  };

  const peakAmbitionEls = buildRoadmapItems({
    blockName: "Peak Ambition", x: 1123, y: 359, yOffsetOverride: 50,
    width: "22%", height: "60%", date: peakAmbitionDate,
    content: peakAmbitionContent, label: "Peak Ambition",
  });
  const peakObjectiveEls = buildRoadmapItems({
    blockName: "Peak Ambition Objectives", x: 1111, y: 488,
    width: "20%", height: "35%", date: peakObjectiveDate,
    content: peakObjectiveContent, label: "Peak Ambition Objectives",
  });
  const threeYearObjectivesEls = buildRoadmapItems({
    blockName: "3 Year Objectives", x: 1154, y: 620,
    width: "20%", height: "35%", date: threeYearObjectivesDate,
    content: threeYearObjectivesContent,
  });
  const oneYearObjectivesEls = buildRoadmapItems({
    blockName: "1 Year Objectives", x: 1118, y: 755,
    width: "20%", height: "35%", date: oneYearObjectivesDate,
    content: oneYearObjectivesContent,
  });
  const qObjectivesEls = buildRoadmapItems({
    blockName: "Quarterly Objectives", x: 833, y: 890,
    yOffsetOverride: -150, blankYOffsetOverride: -50,
    width: "20%", height: "35%", date: qObjectivesDate,
    content: qObjectivesContent,
  });

  const autoSizedText = (
    title: string,
    content: string,
    blockName: string,
    isHovering: boolean,
  ) => {
    const isEditing = editingBlock === blockName;
    const currentContent = editContent[blockName] ?? content ?? "";

    return (
      <>
        <div style={{ display: "flex", justifyContent: "space-between",
          alignItems: "center", marginBottom: "10px" }}>
          <div style={{ color: "#234f6d", fontWeight: "500",
            height: "40px", userSelect: "none", flex: 1,
            fontSize: "clamp(14px, 2.5vw, 24px)", lineHeight: "40px",
            overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
            {title}
          </div>
          {(isHovering || isEditing) && (
            <div style={{ display: "flex", gap: "12px", marginLeft: "10px", alignItems: "center" }}>
              {editButton(blockName)}
            </div>
          )}
        </div>
        <div style={{ border: isEditing ? "2px solid #d0d0d0" : "none",
          borderRadius: "4px", paddingRight: isEditing ? "8px" : "0",
          paddingLeft: isEditing ? "8px" : "0" }}>
          <div style={{ margin: 0, color: "#2D6985",
            fontWeight: "500", userSelect: "none", height: "150px",
            fontSize: "16px", lineHeight: "1.5", overflow: "hidden",
            overflowY: isEditing ? "auto" : undefined }}>
            {isEditing ? (
              <textarea
                value={currentContent}
                onChange={(e) =>
                  setEditContent((prev) => ({ ...prev, [blockName]: e.target.value }))
                }
                style={{ width: "100%", height: "140px", resize: "none",
                  border: "none", outline: "none", fontSize: "14px",
                  fontFamily: "inherit", lineHeight: "1.5" }}
              />
            ) : (
              <ReadOnlyEditor content={currentContent} />
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <div
      style={
        embedded
          ? { position: "relative", width: "100%", height: "100%",
              overflow: "hidden", margin: 0, padding: 0 }
          : { position: "fixed", top: 0, left: isCollapsed ? 0 : 190,
              width: "100vw", height: "100vh", overflow: "hidden", margin: 0, padding: 0 }
      }
    >
      <svg width="100%" height="100%" viewBox="0 0 2136 1151" fill="none"
        xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink"
        style={{ display: "block", margin: 0, padding: 0, overflow: "hidden" }}
        preserveAspectRatio="xMinYMin slice">

        <image href={PeakBackground} width="2136" height="1151" x={0} y="0"
          preserveAspectRatio="xMinYMin slice" onClick={handleReset} />

        <svg width="352" height="558" viewBox="0 0 352 558" fill="none"
          x={793} y="371" xmlns="http://www.w3.org/2000/svg">
          <circle cx="301.5" cy="14.5" r="12" {...peakAmbitionEls.ballColor}
            strokeWidth="5" style={{ cursor: "pointer" }}
            onClick={() => handleClick("peakAmbition")} />
          <circle cx="291.5" cy="144.5" r="12" {...peakObjectiveEls.ballColor}
            strokeWidth="5" style={{ cursor: "pointer" }}
            onClick={() => handleClick("peakObjective")} />
          <circle cx="337.5" cy="275.5" r="12" {...threeYearObjectivesEls.ballColor}
            strokeWidth="5" style={{ cursor: "pointer" }}
            onClick={() => handleClick("threeYearObjectives")} />
          <circle cx="300.5" cy="410.5" r="12" strokeWidth="5"
            {...oneYearObjectivesEls.ballColor} style={{ cursor: "pointer" }}
            onClick={() => handleClick("oneYearObjectives")} />
          <circle cx="14.5" cy="543.5" r="12" strokeWidth="5"
            {...qObjectivesEls.ballColor} style={{ cursor: "pointer" }}
            onClick={() => handleClick("qObjectives")} />
        </svg>

        {/* Vision */}
        <foreignObject x={150} y={125} width={600} height="100%"
          onClick={(e) => { if (editingBlock !== "Vision") e.stopPropagation(); }}
          onMouseEnter={() => setVisionHover(true)}
          onMouseLeave={() => setVisionHover(false)}
          style={{ cursor: visionHover || editingBlock === "Vision" ? "default" : "pointer" }}>
          <div style={{ padding: "10px",
            background: visionHover || editingBlock === "Vision"
              ? "rgba(255, 255, 255, 0.85)" : "transparent",
            borderRadius: "8px", transition: "background 0.2s" }}>
            {autoSizedText("Vision", vision, "Vision", visionHover)}
          </div>
        </foreignObject>

        {/* Mission */}
        <foreignObject x={0} y={windowWidth / windowHeight > 2 ? 800 : 900}
          width={750} height="90%"
          onClick={(e) => { if (editingBlock !== "Mission") e.stopPropagation(); }}
          onMouseEnter={() => setMissionHover(true)}
          onMouseLeave={() => setMissionHover(false)}
          style={{ cursor: missionHover || editingBlock === "Mission" ? "default" : "pointer" }}>
          <div style={{ width: "100%", height: "100%", padding: "10px",
            paddingLeft: "155px", paddingRight: "105px", paddingTop: "25px",
            background: missionHover || editingBlock === "Mission"
              ? "linear-gradient(270deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.95) 30%)"
              : "linear-gradient(270deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.80) 30%)",
            marginRight: "-100px", borderRadius: "8px", transition: "background 0.2s" }}>
            {autoSizedText("Mission", mission, "Mission", missionHover)}
          </div>
        </foreignObject>

        {peakAmbitionEls.roadmapItemText}
        {peakObjectiveEls.roadmapItemText}
        {qObjectivesEls.roadmapItemText}
        {oneYearObjectivesEls.roadmapItemText}
        {threeYearObjectivesEls.roadmapItemText}

        {peakAmbitionHover && peakAmbitionEls.roadmapItemModal}
        {peakObjectiveHover && peakObjectiveEls.roadmapItemModal}
        {qObjectivesHover && qObjectivesEls.roadmapItemModal}
        {oneYearObjectivesHover && oneYearObjectivesEls.roadmapItemModal}
        {threeYearObjectivesHover && threeYearObjectivesEls.roadmapItemModal}
      </svg>
    </div>
  );
};
